/**
 * Task Type Definitions (Modular Task Types)
 *
 * A `TaskTypeDef` is an org-defined, customizable classification for tasks
 * (Bug, Feature, Request, Incident, ...) — the same pattern as ticket types.
 * Each type bundles:
 *   - vocabulary (name, icon, color)
 *   - an OPTIONAL workflow (its own set of statuses, scoped by typeId)
 *   - a set of CAPABILITY toggles that turn behaviors on/off (modular)
 *   - visibility scoping
 *
 * Pre-configured but customizable: each project type seeds a sensible default
 * set of task types (the universal `task` type plus a few specialised ones).
 * Orgs can install more from the template registry (TASK_TYPE_TEMPLATES), then
 * rename/recolor/toggle/edit them. Only `key` is immutable (API stability).
 *
 * A type that defines its own `statuses` drives a distinct workflow for its
 * tasks; a type with no statuses (e.g. the generic `task`) inherits the
 * project/group/org status configuration like tasks always have.
 */

import { ProjectTypeEnum } from './project'
import { StatusConfig } from './templates'
import { TaskStatusTypeEnum } from './task-status'

/* -------------------------------------------------------------------------- */
/*  Capabilities                                                              */
/* -------------------------------------------------------------------------- */

/** The modular behavior toggles available on a task type. */
export interface TaskCapabilities {
  /** Tasks of this type can have subtasks. */
  allowsSubtasks: boolean
  /** Tasks of this type must have an assignee. */
  requiresAssignee: boolean
  /** Tasks of this type must carry an estimate (hours / story points). */
  requiresEstimate: boolean
  /** Tasks of this type route through an approval workflow. */
  requiresApproval: boolean
  /** Tasks of this type track an SLA timer. */
  hasSlaTimer: boolean
  /** New tasks of this type default to private visibility. */
  privateByDefault: boolean
}

export type TaskCapabilityKey = keyof TaskCapabilities

/** Safe baseline — only the near-universal behavior on. */
export const DEFAULT_TASK_CAPABILITIES: TaskCapabilities = {
  allowsSubtasks: true,
  requiresAssignee: false,
  requiresEstimate: false,
  requiresApproval: false,
  hasSlaTimer: false,
  privateByDefault: false,
}

/** UI metadata for the capability checkboxes on the settings page. */
export const TASK_CAPABILITY_META: Record<
  TaskCapabilityKey,
  { label: string; description: string }
> = {
  allowsSubtasks: {
    label: 'Allow subtasks',
    description: 'Let tasks of this type be broken down into subtasks.',
  },
  requiresAssignee: {
    label: 'Requires assignee',
    description: 'Tasks of this type must have someone assigned.',
  },
  requiresEstimate: {
    label: 'Requires estimate',
    description: 'Tasks of this type must carry an estimate before work starts.',
  },
  requiresApproval: {
    label: 'Requires approval',
    description: 'Route tasks of this type through a submit → approve workflow.',
  },
  hasSlaTimer: {
    label: 'SLA timer',
    description: 'Track a response/resolution deadline and flag breaches.',
  },
  privateByDefault: {
    label: 'Private by default',
    description: 'New tasks are visible only to assignees and admins.',
  },
}

/* -------------------------------------------------------------------------- */
/*  Visibility                                                                */
/* -------------------------------------------------------------------------- */

export enum TaskTypeVisibilityScope {
  /** Visible to everyone in the org (default). */
  ALL = 'ALL',
  /** Visible only to members holding one of `visibleToRoleKeys`. */
  RESTRICTED = 'RESTRICTED',
}

/* -------------------------------------------------------------------------- */
/*  Display                                                                   */
/* -------------------------------------------------------------------------- */

export enum TaskTypeDisplayStyle {
  /** Render the phosphor `icon`. */
  ICON = 'ICON',
  /** Render the first letter of `name` in a colored chip. */
  LETTER = 'LETTER',
}

/** The letter shown in LETTER-mode chips — first character of name, uppercased. */
export function getTaskTypeLetter(name?: string | null): string {
  return (name?.trim()[0] ?? '?').toUpperCase()
}

/* -------------------------------------------------------------------------- */
/*  Type definition records                                                   */
/* -------------------------------------------------------------------------- */

/** Full TaskTypeDef record as stored/returned. */
export interface TaskTypeDef extends TaskCapabilities {
  id: string
  organizationId: string
  key: string
  name: string
  pluralName: string | null
  description: string | null
  icon: string | null
  color: string | null
  displayStyle: TaskTypeDisplayStyle
  isEnabled: boolean
  isSystem: boolean
  isArchived: boolean
  order: number
  defaultStatusId: string | null
  requiredFields: string[]
  customFieldDefIds: string[]
  defaultAssigneeId: string | null
  defaultPriority: string | null
  slaTargetMinutes: number | null
  visibilityScope: TaskTypeVisibilityScope
  visibleToRoleKeys: string[]
  createdAt: Date
  updatedAt: Date
}

/** Lightweight summary embedded on task read payloads for icon/color display. */
export interface TaskTypeSummary {
  id: string
  key: string
  name: string
  icon: string | null
  color: string | null
  displayStyle: TaskTypeDisplayStyle
}

export interface CreateTaskTypeDef {
  key: string
  name: string
  pluralName?: string | null
  description?: string | null
  icon?: string | null
  color?: string | null
  displayStyle?: TaskTypeDisplayStyle
  order?: number
  defaultStatusId?: string | null
  requiredFields?: string[]
  customFieldDefIds?: string[]
  defaultAssigneeId?: string | null
  defaultPriority?: string | null
  slaTargetMinutes?: number | null
  visibilityScope?: TaskTypeVisibilityScope
  visibleToRoleKeys?: string[]
  capabilities?: Partial<TaskCapabilities>
}

export interface UpdateTaskTypeDef {
  name?: string
  pluralName?: string | null
  description?: string | null
  icon?: string | null
  color?: string | null
  displayStyle?: TaskTypeDisplayStyle
  isEnabled?: boolean
  isArchived?: boolean
  order?: number
  defaultStatusId?: string | null
  requiredFields?: string[]
  customFieldDefIds?: string[]
  defaultAssigneeId?: string | null
  defaultPriority?: string | null
  slaTargetMinutes?: number | null
  visibilityScope?: TaskTypeVisibilityScope
  visibleToRoleKeys?: string[]
  capabilities?: Partial<TaskCapabilities>
}

/* -------------------------------------------------------------------------- */
/*  Per-project subset                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A project's chosen subset of the org task-type master list. An empty subset
 * for a project means "all enabled org types" (legacy fallback).
 */
export interface ProjectTaskTypeDef {
  id: string
  projectId: string
  taskTypeDefId: string
  order: number
}

/** Add a single type to a project's subset. */
export interface AddProjectTaskTypeDto {
  taskTypeDefId: string
}

/** Replace/reorder a project's whole subset. */
export interface SetProjectTaskTypesDto {
  taskTypeDefIds: string[]
}

/* -------------------------------------------------------------------------- */
/*  Template registry (pre-configured, installable)                          */
/* -------------------------------------------------------------------------- */

export enum TaskTypeBundle {
  GENERIC = 'generic',
  PRODUCT_ENG = 'product_eng',
  OPERATIONS = 'operations',
}

export const TASK_TYPE_BUNDLE_LABELS: Record<TaskTypeBundle, string> = {
  [TaskTypeBundle.GENERIC]: 'General',
  [TaskTypeBundle.PRODUCT_ENG]: 'Product & Engineering',
  [TaskTypeBundle.OPERATIONS]: 'Operations',
}

/** An installable, pre-configured task type. */
export interface TaskTypeTemplate {
  key: string
  name: string
  pluralName: string
  description: string
  icon: string
  color: string
  /** Templates ship an icon, so they render in ICON mode unless overridden. */
  displayStyle?: TaskTypeDisplayStyle
  bundle: TaskTypeBundle
  capabilities: Partial<TaskCapabilities>
  /**
   * The type's optional workflow. When present, tasks of this type resolve
   * these statuses (scoped by typeId) instead of the project workflow. When
   * omitted, the type inherits the project/group/org status configuration.
   */
  statuses?: StatusConfig[]
  defaultStatusId?: string
  requiredFields?: string[]
  defaultPriority?: string
  visibilityScope?: TaskTypeVisibilityScope
  /** Installed automatically for every new project, regardless of type. */
  seededByDefault?: boolean
  /** Project types that additionally seed this type at creation. */
  seededFor?: ProjectTypeEnum[]
}

/* ----- Reusable status workflows ------------------------------------------ */

const s = (
  id: string,
  name: string,
  color: string,
  statusTypeId: TaskStatusTypeEnum,
  order: number,
  isDefault = false,
): StatusConfig => ({ id, name, color, statusTypeId, order, isDefault })

const BUG_STATUSES: StatusConfig[] = [
  s('open', 'Open', '#6B7280', TaskStatusTypeEnum.NOT_STARTED, 0, true),
  s('in-progress', 'In Progress', '#3B82F6', TaskStatusTypeEnum.IN_PROGRESS, 1),
  s('in-review', 'In Review', '#F59E0B', TaskStatusTypeEnum.IN_PROGRESS, 2),
  s('fixed', 'Fixed', '#10B981', TaskStatusTypeEnum.COMPLETED, 3),
  s('wont-fix', "Won't Fix", '#334155', TaskStatusTypeEnum.CANCELED, 4),
]

const FEATURE_STATUSES: StatusConfig[] = [
  s('backlog', 'Backlog', '#6B7280', TaskStatusTypeEnum.NOT_STARTED, 0, true),
  s('planned', 'Planned', '#8B5CF6', TaskStatusTypeEnum.NOT_STARTED, 1),
  s('in-progress', 'In Progress', '#3B82F6', TaskStatusTypeEnum.IN_PROGRESS, 2),
  s('in-review', 'In Review', '#F59E0B', TaskStatusTypeEnum.IN_PROGRESS, 3),
  s('shipped', 'Shipped', '#10B981', TaskStatusTypeEnum.COMPLETED, 4),
]

const INCIDENT_STATUSES: StatusConfig[] = [
  s('reported', 'Reported', '#DC2626', TaskStatusTypeEnum.NOT_STARTED, 0, true),
  s('investigating', 'Investigating', '#F97316', TaskStatusTypeEnum.IN_PROGRESS, 1),
  s('mitigated', 'Mitigated', '#3B82F6', TaskStatusTypeEnum.IN_PROGRESS, 2),
  s('resolved', 'Resolved', '#10B981', TaskStatusTypeEnum.COMPLETED, 3),
]

/**
 * The three-column workflow seeded for Personal accounts. Attached to the
 * default `task` type at creation (see seedDefaultTaskTypes) so personal tasks
 * use a friendly To Do / Doing / Done board instead of the business catalog.
 */
export const PERSONAL_TASK_STATUSES: StatusConfig[] = [
  s('todo', 'To Do', '#6B7280', TaskStatusTypeEnum.NOT_STARTED, 0, true),
  s('doing', 'Doing', '#3B82F6', TaskStatusTypeEnum.IN_PROGRESS, 1),
  s('done', 'Done', '#10B981', TaskStatusTypeEnum.COMPLETED, 2),
]

/** Default status id personal `task`-typed items land in. */
export const PERSONAL_TASK_DEFAULT_STATUS_ID = 'todo'

/* ----- The catalog -------------------------------------------------------- */

export const TASK_TYPE_TEMPLATES: TaskTypeTemplate[] = [
  // --- General ---
  {
    key: 'task',
    name: 'Task',
    pluralName: 'Tasks',
    description: 'General work item — inherits the project workflow.',
    icon: 'CheckSquare',
    color: '#64748B',
    // The neutral default renders as a letter chip rather than an icon.
    displayStyle: TaskTypeDisplayStyle.LETTER,
    bundle: TaskTypeBundle.GENERIC,
    capabilities: {},
    // No `statuses` — inherits project/group/org statuses (the legacy behavior).
    seededByDefault: true,
  },
  {
    key: 'request',
    name: 'Request',
    pluralName: 'Requests',
    description: 'An incoming ask routed into the project.',
    icon: 'Tray',
    color: '#0EA5E9',
    bundle: TaskTypeBundle.GENERIC,
    capabilities: {},
    seededFor: [ProjectTypeEnum.BASIC, ProjectTypeEnum.WATERFALL],
  },
  {
    key: 'chore',
    name: 'Chore',
    pluralName: 'Chores',
    description: 'Maintenance and upkeep work.',
    icon: 'Broom',
    color: '#6B7280',
    bundle: TaskTypeBundle.OPERATIONS,
    capabilities: {},
  },

  // --- Product & Engineering ---
  {
    key: 'bug',
    name: 'Bug',
    pluralName: 'Bugs',
    description: 'A defect or regression to fix.',
    icon: 'Bug',
    color: '#EF4444',
    bundle: TaskTypeBundle.PRODUCT_ENG,
    capabilities: { requiresAssignee: false },
    statuses: BUG_STATUSES,
    defaultStatusId: 'open',
    defaultPriority: 'HIGH',
    seededFor: [ProjectTypeEnum.KANBAN, ProjectTypeEnum.SCRUM],
  },
  {
    key: 'feature',
    name: 'Feature',
    pluralName: 'Features',
    description: 'New capability or enhancement to build.',
    icon: 'Sparkle',
    color: '#8B5CF6',
    bundle: TaskTypeBundle.PRODUCT_ENG,
    capabilities: {},
    statuses: FEATURE_STATUSES,
    defaultStatusId: 'backlog',
    seededFor: [ProjectTypeEnum.KANBAN, ProjectTypeEnum.SCRUM],
  },
  {
    key: 'improvement',
    name: 'Improvement',
    pluralName: 'Improvements',
    description: 'A refinement to existing functionality.',
    icon: 'TrendUp',
    color: '#14B8A6',
    bundle: TaskTypeBundle.PRODUCT_ENG,
    capabilities: {},
    seededFor: [ProjectTypeEnum.KANBAN, ProjectTypeEnum.SCRUM],
  },
  {
    key: 'incident',
    name: 'Incident',
    pluralName: 'Incidents',
    description: 'An urgent issue needing rapid response.',
    icon: 'Warning',
    color: '#DC2626',
    bundle: TaskTypeBundle.OPERATIONS,
    capabilities: { hasSlaTimer: true },
    statuses: INCIDENT_STATUSES,
    defaultStatusId: 'reported',
    defaultPriority: 'URGENT',
  },
]

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

/** The neutral fallback type every org has; also the backfill target. */
export const DEFAULT_TASK_TYPE_KEY = 'task'

/** Types installed automatically for every new project, regardless of type. */
export const DEFAULT_SEEDED_TASK_TYPE_KEYS: string[] =
  TASK_TYPE_TEMPLATES.filter(tpl => tpl.seededByDefault).map(tpl => tpl.key)

/**
 * The task-type keys to seed for a new project given its project type. Always
 * includes the universal set plus any types that opt into this project type.
 */
export function getDefaultSeededTaskTypeKeys(
  projectType?: string | null,
): string[] {
  const normalized = projectType?.toUpperCase() as ProjectTypeEnum | undefined
  const keys = new Set<string>(DEFAULT_SEEDED_TASK_TYPE_KEYS)
  for (const tpl of TASK_TYPE_TEMPLATES) {
    if (normalized && tpl.seededFor?.includes(normalized)) {
      keys.add(tpl.key)
    }
  }
  // Preserve catalog order so seeded `order` indices stay deterministic.
  return TASK_TYPE_TEMPLATES.filter(tpl => keys.has(tpl.key)).map(tpl => tpl.key)
}

export function getTaskTypeTemplate(key: string): TaskTypeTemplate | undefined {
  return TASK_TYPE_TEMPLATES.find(tpl => tpl.key === key)
}

export function getTaskTypeTemplatesByBundle(
  bundle: TaskTypeBundle,
): TaskTypeTemplate[] {
  return TASK_TYPE_TEMPLATES.filter(tpl => tpl.bundle === bundle)
}

/** Merge a template's partial capabilities onto the safe baseline. */
export function resolveTaskCapabilities(
  partial?: Partial<TaskCapabilities>,
): TaskCapabilities {
  return { ...DEFAULT_TASK_CAPABILITIES, ...(partial ?? {}) }
}

/** Whether a task type exposes a given capability. */
export function taskTypeCan(
  typeDef: Pick<TaskTypeDef, TaskCapabilityKey> | null | undefined,
  capability: TaskCapabilityKey,
): boolean {
  return typeDef?.[capability] === true
}
